import { Box, Flex, Stack, Text, UnstyledButton } from "@mantine/core"
import type { AgencyAffiliation } from "../agencyAffiliationsSection/AgencyAffiliationsTypes"
import AgencyAddressBlock from "../agencyAffiliationsSection/subsections/AgencyAddressBlock"
import styles from "./AgencyCard.module.css"
import {
	useAEMDictionary,
	AEM_DICTIONARY_COLLECTIONS
} from "@AEM/hooks/useAEMDictionary"
import { useAEMIcons } from "@AEM/hooks/useAEMIcons"
import { AEMSvg } from "@components/AEMSvg"

interface AgencyCardProps {
	agency: AgencyAffiliation
	/** Text shown in the banner strip at the top of the card */
	bannerLabel: string
	/** "active" = green banner; "neutral" = gray banner. Default: "neutral" */
	bannerVariant?: "active" | "neutral"
	/** When provided the banner becomes a full-width centered clickable action instead of a label */
	onBannerAction?: () => void
	/** Shows a "Make Default Agency" link in the card body */
	onMakeDefault?: () => void
	/** When provided alongside onBannerAction, shows a radio indicator in the banner */
	isSelected?: boolean
	/** Optional max-width constraint passed to the outer Box */
	maw?: number | string
	/** Optional width passed to the outer Box */
	w?: number | string

	bannerActionAlign?: "center" | "start"

	bannerIcon?: React.ReactNode
}

const AgencyCard = ({
	agency,
	bannerLabel,
	bannerVariant = "neutral",
	onBannerAction,
	onMakeDefault,
	isSelected,
	maw,
	w,
	bannerActionAlign = "center",
	bannerIcon
}: AgencyCardProps) => {
	const { dictionary } = useAEMDictionary(
		AEM_DICTIONARY_COLLECTIONS.REGISTRATION
	)
	const { icons } = useAEMIcons()
	return (
		<Box className={styles.card} maw={maw} w={w}>
			{onBannerAction ? (
				<UnstyledButton
					className={styles.bannerAction}
					onClick={onBannerAction}
					aria-label={bannerLabel}
					style={
						bannerActionAlign === "start"
							? { justifyContent: "flex-start", padding: "0 24px", gap: 12 }
							: undefined
					}
				>
					{isSelected !== undefined && (
						<Box
							className={isSelected ? styles.radioSelected : styles.radio}
							aria-hidden
						/>
					)}
					{bannerIcon && <Box style={{ flexShrink: 0 }}>{bannerIcon}</Box>}
					<Text
						fz={12}
						fw={600}
						lh={1.4}
						c="var(--text-color-primary)"
						td={bannerIcon ? "underline" : undefined}
						ml={bannerIcon ? 8 : undefined}
					>
						{bannerLabel}
					</Text>
				</UnstyledButton>
			) : (
				<Box
					className={
						bannerVariant === "active"
							? styles.bannerActive
							: styles.bannerNeutral
					}
				>
					<Text
						fz={14}
						fw={600}
						lh={1.4}
						c={
							bannerVariant === "active"
								? "var(--mantine-color-royal-49)"
								: "var(--text-color-primary)"
						}
					>
						{bannerLabel}
					</Text>
				</Box>
			)}
			<Box className={styles.body} p={8}>
				<Flex
					px={16}
					py={12}
					gap={8}
					align="flex-start"
					direction={{ base: "column", sm: "row" }}
				>
					<Stack gap={4} style={{ flex: 1 }}>
						<Text fz={24} fw={600} lh={1.4} c="var(--text-color-primary)">
							{agency.agencyName}
						</Text>
						{onMakeDefault ? (
							<UnstyledButton
								className={styles.actionLink}
								onClick={onMakeDefault}
								aria-label={`Make ${agency.agencyName} the default agency`}
							>
								{dictionary?.maMakeDefaultAgencyText ?? ""}
								{icons["Cheveron-right.svg"] && (
									<AEMSvg svgData={icons["Cheveron-right.svg"]} w={12} h={12} />
								)}
							</UnstyledButton>
						) : agency.isDefault ? (
							<Box className={styles.defaultBadge}>
								<Text fz={10} fw={700} c="white" lh={1}>
									{dictionary?.maDefaultAgencyText ?? ""}
								</Text>
							</Box>
						) : null}
					</Stack>
					<Box style={{ flex: 1 }}>
						<AgencyAddressBlock agency={agency} />
					</Box>
				</Flex>
			</Box>
		</Box>
	)
}

export default AgencyCard
